import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../../hooks/api-hook";
import { AppContext } from "../../setup/context";
import {
    Box,
    ColumnLayout,
    SpaceBetween,
    Spinner,
    Tabs,
    TabsProps,
} from "@amzn/awsui-components-react";
import { Builder } from "builder-pattern";
import { ErrorFlashbar } from "../common/ErrorFlashbar";
import { SearchInputBar } from "../search/SearchInputBar";
import { Instance, EntityPermission, Stage } from "@amzn/ask-legal-domain";
import { HomePageRecommendationWidget } from "../home-page-widgets/home-page-recommendation-widget";
import "../../styles/home-page/instance-home-page.scss";
import "../../styles/home-page/instance-home-widgets.scss";
import "../../styles/component/layout/NavigationFooter.scss";

const DEFAULT_BACKGROUND_IMAGE = require("../../assets/instance-home-background-images/default.png").default;

interface HomePageProps {
    onInstanceSelect?: (instanceId: string) => void;
}

export const HomePage: React.FC<HomePageProps> = (props) => {
    const context = React.useContext(AppContext);
    const navigate = useNavigate();

    const instanceLoadAllApiRunner = useAPI(context.getInstanceAPI().loadAll);

    const [myTeamInstances, setMyTeamInstances] = React.useState<Instance[]>([]);
    const [publicInstances, setPublicInstances] = React.useState<Instance[]>([]);
    const [allInstances, setAllInstances] = React.useState<Instance[]>([]);
    const [activeTabId, setActiveTabId] = React.useState("all-team-sites");

    // Get current stage for public instances
    const getCurrentStage = (): string => {
        const stage = context.getStage() as Stage;
        return [Stage.Prod, Stage.Gamma, Stage.Beta].includes(stage) ? stage : Stage.Dev;
    };

    // Filter public instances based on current stage only
    const getPublicInstancesForStage = (instances: Instance[]): Instance[] => {
        const currentStage = getCurrentStage();
        const publicInstanceIds = EntityPermission.ENABLED_PUBLIC_INSTANCES[currentStage] || [];

        return instances.filter(instance =>
            publicInstanceIds.includes(instance.id)
        );
    };

    // Instance menu selection
    const handleInstanceSelect = (instance: Instance) => {
        context.setPreference(instance.id);
        if (props.onInstanceSelect) {
            props.onInstanceSelect(instance.id);
        } else {
            navigate(`/instance/${instance.id}/home`);
        }
    };

    // Load instances
    React.useEffect(() => {
        instanceLoadAllApiRunner.submitRun({});
    }, []);

    React.useEffect(() => {
        if (instanceLoadAllApiRunner.status === "Succeeded") {
            const instances = [...instanceLoadAllApiRunner.data.output].sort((a, b) => a.name.localeCompare(b.name));
            const publicInstancesForStage = getPublicInstancesForStage(instances);
            const publicInstanceIds = new Set(publicInstancesForStage.map(i => i.id));

            setMyTeamInstances(instances.filter(i => !publicInstanceIds.has(i.id)));
            setPublicInstances(publicInstancesForStage);
            setAllInstances(instances);
        }
    }, [instanceLoadAllApiRunner.status]);

    // Group instances into 3 columns and show teamsites list from top to bottom)
    const getInstanceColumns = (instances: Instance[]) => {
        const perCol = Math.ceil(instances.length / 3);
        return [instances.slice(0, perCol), instances.slice(perCol, perCol * 2), instances.slice(perCol * 2)];
    };

    const renderTeamSitesList = (instances: Instance[], showPublicBadge: boolean = true) => {
        const instanceColumns = getInstanceColumns(instances);

        return (
            <div>
                {instanceLoadAllApiRunner.status === "Running" ? (
                    <Box textAlign="center">
                        <Spinner size="large" />
                        <Box variant="p">Loading team sites.</Box>
                    </Box>
                ) : instances.length === 0 ? (
                    <Box textAlign="center" color="text-body-secondary" margin={{ top: "xxl" }}>
                        <em>No team sites found</em>
                    </Box>
                ) : (
                    <div className="navtab-content">
                        <ColumnLayout columns={3}>
                            {instanceColumns.map((columnInstances, columnIndex) => (
                                <div key={columnIndex} className="navgroup">
                                    <ColumnLayout columns={1} disableGutters>
                                        {columnInstances.map((instance) => (
                                            <div
                                                key={instance.id}
                                                className="navgroup-action"
                                            >
                                                <a
                                                    href={`#/instance/${instance.id}/home`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleInstanceSelect(instance);
                                                    }}
                                                    title={"PathFinder Team Site"}
                                                >
                                                    {instance.name}
                                                </a>
                                                {showPublicBadge && publicInstances.some(pub => pub.id === instance.id) && (
                                                    <Box color="text-status-info" fontSize="body-s" display="inline" margin={{ left: "xs" }}>
                                                        Public
                                                    </Box>
                                                )}
                                            </div>
                                        ))}
                                    </ColumnLayout>
                                </div>
                            ))}
                        </ColumnLayout>
                    </div>
                )}
            </div>
        );
    };

    const tabs = [
        Builder<TabsProps.Tab>()
            .id("all-team-sites")
            .label("All Team Sites")
            .content(renderTeamSitesList(allInstances))
            .disabled(false)
            .build(),
        Builder<TabsProps.Tab>()
            .id("my-team-sites")
            .label("My Team Sites")
            .content(renderTeamSitesList(myTeamInstances))
            .disabled(false)
            .build(),
        Builder<TabsProps.Tab>()
            .id("public-team-sites")
            .label("Public Team Sites")
            .content(renderTeamSitesList(publicInstances, false))
            .disabled(false)
            .build()
    ];

    return (
        <div className="instance-home-page-theme">
            {/* Background Image */}
            <div className="instance-home-widget-theme" style={{
                backgroundImage: `url(${DEFAULT_BACKGROUND_IMAGE})`,
                backgroundPosition: "top center",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
            }}/>

            {/* Global Search Bar */}
            <div className="instance-home-widget-search-bar">
                <SearchInputBar />
            </div>

            {/* Recommendation Widget */}
            <div className="px-5">
                <HomePageRecommendationWidget />
            </div>

            {/* Browse Team Sites Section */}
            <div className="px-5">
                <div className="home-page-sub-header">
                    <p className="title">Browse Team Sites</p>
                </div>
                <Tabs
                    className="awsui-polaris-dark-mode"
                    activeTabId={activeTabId}
                    onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
                    tabs={tabs}
                />
                {instanceLoadAllApiRunner.status === "Error" && (
                    <ErrorFlashbar error={instanceLoadAllApiRunner.data.err} />
                )}
            </div>
        </div>
    );
};
